"use client";

import { ReactNode, useEffect, useRef } from "react";

import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";

import { cn } from "@/utils/cn";

import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from "./EmblaComponents/EmblaCarouselArrowButtons";
import {
  DotButton,
  useDotButton,
} from "./EmblaComponents/EmblaCarouselDotButton";
import styles from "./style.module.scss";

interface Props {
  main: ReactNode;
  length: number;
  setCurrentSlide?: (val: number) => void;
  startIndex?: number;
  showCounter?: boolean;
  counterPosition?: "left" | "center" | "right";
  showPagination?: boolean;
  className?: string;
  navigationPosition?: "center" | "bottom";
  navigationSize?: "small" | "large";
  getEmblaApi?: (api: EmblaCarouselType | undefined) => void;
  loop?: boolean;
  isModal?: boolean;
}

export const CustomSlider = ({
  main,
  length,
  setCurrentSlide,
  startIndex = 0,
  showCounter = false,
  counterPosition = "left",
  className,
  navigationPosition = "bottom",
  navigationSize = "small",
  getEmblaApi,
  showPagination = true,
  loop = false,
  isModal = false,
}: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    dragFree: false,
    duration: 40,
    startIndex: startIndex,
    containScroll: "trimSnaps",
    loop: loop,
  });
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const dotsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (getEmblaApi) {
      getEmblaApi(emblaApi);
    }
  }, [emblaApi, getEmblaApi]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", () => {
        const slide = emblaApi.selectedScrollSnap();
        if (setCurrentSlide) {
          setCurrentSlide(slide);
        }

        if (dotsContainerRef?.current) {
          const activeDot = dotsContainerRef.current.children[
            slide
          ] as HTMLElement;

          const scrollLeft =
            activeDot.offsetLeft +
            activeDot.offsetWidth / 2 -
            dotsContainerRef.current.offsetWidth / 2;
          dotsContainerRef.current.scrollTo({
            left: scrollLeft,
            behavior: "smooth",
          });
        }
      });

      emblaApi.reInit(); // Перезапуск API при изменении ширины
    }
  }, [setCurrentSlide, emblaApi, length]);

  const navPositionClasses = {
    center: "top-1/2 -translate-y-1/2",
    bottom: "bottom-[5%]",
  };

  const counterPositionClasses = {
    left: "absolute bottom-[1.5rem] left-[2.5rem]",
    center: "absolute bottom-[1.5rem] left-1/2 -translate-x-1/2",
    right: "absolute bottom-[1.5rem] right-[2.5rem]",
  };

  return (
    <section className={cn(styles.embla, className)}>
      <div
        className={styles.embla__viewport}
        ref={emblaRef}
      >
        {main}
      </div>

      {length > 1 && (
        <>
          <PrevButton
            className={cn(
              navPositionClasses[navigationPosition],
              navigationSize === "large" && styles.large,
            )}
            onClick={(e) => {
              e.stopPropagation();
              onPrevButtonClick();
            }}
            disabled={prevBtnDisabled}
          />
          <NextButton
            className={cn(
              navPositionClasses[navigationPosition],
              navigationSize === "large" && styles.large,
            )}
            onClick={(e) => {
              e.stopPropagation();
              onNextButtonClick();
            }}
            disabled={nextBtnDisabled}
          />

          {showCounter && (
            <div
              className={cn(
                styles.counter,
                counterPositionClasses[counterPosition],
              )}
            >{`${selectedIndex + 1} / ${length}`}</div>
          )}

          {showPagination && (
            <div
              className={cn(styles.embla__dots, isModal ? styles.modal : "")}
              ref={dotsContainerRef}
            >
              {scrollSnaps.map((_, index) => (
                <DotButton
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDotButtonClick(index);
                  }}
                  className={cn(
                    styles.embla__dot,
                    index === selectedIndex ? styles.selected : "",
                  )}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};
